import React, { useState } from "react";
import { useInstallSystemWorkflowsMutation } from "@/store/admin_api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { apiErrorMessage } from "@/util/apiError";
import { toast } from "sonner";

interface InstallSystemWorkflowsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function InstallSystemWorkflowsDialog({ open, onOpenChange }: InstallSystemWorkflowsDialogProps) {
  const [override, setOverride] = useState(false);
  const [installWorkflows, { isLoading }] = useInstallSystemWorkflowsMutation();

  async function install() {
    try {
      const result = await installWorkflows({ override }).unwrap();
      toast.success(`System workflows installed across ${result.organisations} organisations`);
      setOverride(false);
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to install system workflows"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Install system workflows</DialogTitle>
          <DialogDescription>
            Adds any system workflow an organisation is missing. Organisations that already have them are left alone.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border p-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="override-existing"
              checked={override}
              onCheckedChange={(checked) => setOverride(checked === true)}
              disabled={isLoading}
            />
            <div className="space-y-1">
              <Label htmlFor="override-existing" className="font-semibold">
                Override if it exists
              </Label>
              <p className="text-sm text-muted-foreground">
                Also updates workflows already installed at an older version, replacing their name, trigger and every
                action with the current definition. Any changes an organisation made to those workflows are lost. Past
                runs are kept, and anything already at the current version is untouched.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={install} disabled={isLoading}>
            {isLoading ? "Installing..." : override ? "Install and override" : "Install missing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
